import React, { useEffect, useState } from "react";
import "./PollingQuestions.css";
import { Button, Col, Container, Row, Modal, Form, Dropdown } from "react-bootstrap";
import { FaPlus, FaPoll, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import apiCall from "../../API/API";

const PollingQuestions = () => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    question: "",
    options: ["", ""],
    points: ""
  });
  const [editingPoll, setEditingPoll] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deletingPoll, setDeletingPoll] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchPolls();
  }, []);

  const fetchPolls = async () => {
    try {
      setLoading(true);
      const { data, error } = await apiCall("GET", "/get-all-poll");
      if (error) {
        toast.error(error.message || "Failed to fetch polls");
      } else if (data?.success) {
        setPolls(data.data || []);
      }
    } catch (error) {
      toast.error("An error occurred while fetching polls");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  const addOption = () => {
    if (formData.options.length < 4) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, ""]
      }));
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        options: newOptions
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.question.trim()) {
      toast.error("Please enter a question");
      return;
    }

    const validOptions = formData.options.filter(option => option.trim());
    if (validOptions.length < 2) {
      toast.error("Please provide at least 2 options");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        question: formData.question.trim(),
        options: validOptions,
        points: formData.points.trim() || "0"
      };

      const { data, error } = await apiCall("POST", "/add-poll", payload);
      if (error) {
        toast.error(error.message || "Failed to create poll");
      } else if (data?.success) {
        toast.success(data.message || "Poll created successfully!");
        setShowModal(false);
        resetForm();
        fetchPolls(); // Refresh the list
      }
    } catch (error) {
      toast.error("An error occurred while creating the poll");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      question: "",
      options: ["", ""],
      points: ""
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleStatusChange = async (pollId, newStatus) => {
    try {
      setLoading(true);
      
      const { data, error } = await apiCall("POST", "/edit-poll-status", {
        pollId: pollId,
        status: newStatus
      });
      
      if (error) {
        toast.error(error.message || "Failed to update poll status");
      } else if (data?.success) {
        toast.success(data.message || "Poll status updated successfully!");
        fetchPolls(); // Refresh the list
      }
    } catch (error) {
      toast.error("An error occurred while updating poll status");
    } finally {
      setLoading(false);
    }
  };

  const handleEditPoll = (poll) => {
    setEditingPoll(poll);
    setFormData({
      question: poll.question,
      options: poll.options.map(opt => opt.text),
      points: poll.points || ""
    });
    setShowEditModal(true);
  };

  const handleUpdatePoll = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.question.trim()) {
      toast.error("Please enter a question");
      return;
    }

    const validOptions = formData.options.filter(option => option.trim());
    if (validOptions.length < 2) {
      toast.error("Please provide at least 2 options");
      return;
    }

    try {
      setLoading(true);
      console.log(formData.points);
      const payload = {
        question: formData.question.trim(),
        options: validOptions,
        points: formData.points.trim() || "0"
      };

      const { data, error } = await apiCall("POST", "/edit-poll", {
        pollId: editingPoll.pollId,
        ...payload
      });
      
      if (error) {
        toast.error(error.message || "Failed to update poll");
      } else if (data?.success) {
        toast.success(data.message || "Poll updated successfully!");
        setShowEditModal(false);
        setEditingPoll(null);
        resetForm();
        fetchPolls(); // Refresh the list
      }
    } catch (error) {
      toast.error("An error occurred while updating the poll");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingPoll(null);
    resetForm();
  };

  const handleDeletePoll = (poll) => {
    setDeletingPoll(poll);
    setShowDeleteModal(true);
  };

  const confirmDeletePoll = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await apiCall("POST", "/delete-poll", {
        pollId: deletingPoll.pollId
      });
      
      if (error) {
        toast.error(error.message || "Failed to delete poll");
      } else if (data?.success) {
        toast.success(data.message || "Poll deleted successfully!");
        setShowDeleteModal(false);
        setDeletingPoll(null);
        fetchPolls(); // Refresh the list
      }
    } catch (error) {
      toast.error("An error occurred while deleting the poll");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingPoll(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container>
      <section>
        <div className="polling-header">
          <div className="polling-title">
            <h4 className="welcome">
              <FaPoll className="me-2" />
              Polling Questions
            </h4>
            <p className="text-muted">Manage and create polling questions for users</p>
          </div>
          <Button 
            variant="primary" 
            onClick={() => setShowModal(true)}
            className="add-poll-btn"
          >
            <FaPlus className="me-2" />
            Add Polling Question
          </Button>
        </div>

        <Row>
          <Col md="12">
            <div className="polls-container">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : polls.length === 0 ? (
                <div className="text-center py-5">
                  <FaPoll className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                  <h5 className="text-muted">No polls found</h5>
                  <p className="text-muted">Create your first polling question to get started</p>
                </div>
              ) : (
                <div className="polls-grid">
                  {polls.map((poll) => (
                    <div key={poll._id} className="poll-card">
                                             <div className="poll-header">
                         <div className="poll-question-section">
                           <h6 className="poll-question">{poll.question}</h6>
                           {poll.points && (
                             <div className="poll-points">
                               <span className="points-badge">{poll.points} points</span>
                             </div>
                           )}
                         </div>
                         <div className="poll-actions">
                           <Dropdown>
                             <Dropdown.Toggle
                               variant={poll.status === 1 ? 'success' : 'secondary'}
                               size="sm"
                               disabled={loading}
                               className="status-toggle-btn"
                             >
                               {poll.status === 1 ? 'Active' : 'Inactive'}
                             </Dropdown.Toggle>
                             
                             <Dropdown.Menu>
                               <Dropdown.Item 
                                 onClick={() => handleStatusChange(poll.pollId, 1)}
                                 active={poll.status === 1}
                               >
                                 Active
                               </Dropdown.Item>
                               <Dropdown.Item 
                                 onClick={() => handleStatusChange(poll.pollId, 0)}
                                 active={poll.status === 0}
                               >
                                 Inactive
                               </Dropdown.Item>
                             </Dropdown.Menu>
                           </Dropdown>
                           
                                                       <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditPoll(poll)}
                              disabled={loading}
                              className="edit-poll-btn"
                            >
                              Edit
                            </Button>
                            
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeletePoll(poll)}
                              disabled={loading}
                              className="delete-poll-btn"
                              title="Delete Poll"
                            >
                              <FaTrash />
                            </Button>
                         </div>
                       </div>
                                             <div className="poll-options">
                         {poll.optionsWithPercentage.map((option) => (
                           <div 
                             key={option.optionId} 
                             className="poll-option"
                             style={{
                               '--percentage': `${option.percentage}%`
                             }}
                           >
                             <div className="option-content">
                               <span className="option-text">{option.text}</span>
                               <div className="option-stats">
                                 <span className="vote-count">{option.votes} votes</span>
                                 <span className="vote-percentage">{option.percentage}%</span>
                               </div>
                             </div>
                           </div>
                         ))}
                       </div>
                      <div className="poll-footer">
                        <small className="text-muted">
                          Poll ID: {poll.pollId}
                        </small>
                        <small className="text-muted">
                          Created: {formatDate(poll.createdAt)}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </section>

      {/* Add Poll Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Polling Question</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Question</Form.Label>
              <Form.Control
                type="text"
                name="question"
                value={formData.question}
                onChange={handleInputChange}
                placeholder="Enter your polling question..."
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Points</Form.Label>
              <Form.Control
                type="number"
                name="points"
                value={formData.points}
                onChange={handleInputChange}
                placeholder="Enter points for this poll (optional)"
                min="0"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Options</Form.Label>
                                 {formData.options.map((option, index) => (
                     <div key={index} className="option-input-group mb-2">
                                               <Form.Control
                          type="text"
                          value={option}
                          onChange={(e) => handleOptionChange(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          required
                        />
                  {formData.options.length > 2 && (
                    <Button
                      type="button"
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeOption(index)}
                      className="remove-option-btn"
                    >
                      ×
                    </Button>
                  )}
                </div>
              ))}
              
              {formData.options.length < 4 && (
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={addOption}
                  className="add-option-btn"
                >
                  <FaPlus className="me-1" />
                  Add Option
                </Button>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Poll'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Poll Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Polling Question</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdatePoll}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Question</Form.Label>
              <Form.Control
                type="text"
                name="question"
                value={formData.question}
                onChange={handleInputChange}
                placeholder="Enter your polling question..."
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Points</Form.Label>
              <Form.Control
                type="number"
                name="points"
                value={formData.points}
                onChange={handleInputChange}
                required
                placeholder="Enter points for this poll"
                min="0"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Options</Form.Label>
              {formData.options.map((option, index) => (
                <div key={index} className="option-input-group mb-2">
                  <Form.Control
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    required
                  />
                  {formData.options.length > 2 && (
                    <Button
                      type="button"
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeOption(index)}
                      className="remove-option-btn"
                    >
                      ×
                    </Button>
                  )}
                </div>
              ))}
              
              {formData.options.length < 4 && (
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={addOption}
                  className="add-option-btn"
                >
                  <FaPlus className="me-1" />
                  Add Option
                </Button>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Poll'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            {/* <FaTrash className="text-danger mb-3" style={{ fontSize: '3rem' }} /> */}
            <h6>Are you sure you want to delete this poll?</h6>
            <p className="text-muted mb-0">
              "{deletingPoll?.question}"
            </p>
            <small className="text-muted">
              This action cannot be undone.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDeletePoll} 
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete Poll'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PollingQuestions;
